import React, { useEffect, useRef, useState } from 'react';
import TextInput from '../TextInput';
import { formatTime, parseTimeInput } from '../../utils/timesheetTime';

interface TimeInputProps {
  /** Committed value, "HH:mm" or "HH:mm:ss", or null for none. */
  value: string | null;
  /** Called on blur or Enter with the normalised "HH:mm", or null when cleared. */
  onCommit: (next: string | null) => void;
  size?: 'sm' | 'md';
  invalid?: boolean;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  autoFocus?: boolean;
  'aria-label'?: string;
  className?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * A clock-time field that accepts what people actually type ("830",
 * "8.30", "1.30pm", "13:30") and normalises it to "HH:mm" when they leave.
 *
 * The text being typed is local state; the committed value is mirrored back
 * in only while the field is not focused, so a refetch cannot rewrite a
 * half-typed time under the caret (the ProgressNumberInput approach).
 * Escape restores the last committed value. Unreadable input stays in the
 * box, marked invalid, and is not committed.
 */
const TimeInput: React.FC<TimeInputProps> = ({
  value,
  onCommit,
  size = 'sm',
  invalid = false,
  disabled = false,
  placeholder = '--:--',
  id,
  autoFocus,
  'aria-label': ariaLabel,
  className = '',
  onKeyDown
}) => {
  const [text, setText] = useState(() => formatTime(value));
  const [unreadable, setUnreadable] = useState(false);
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) {
      setText(formatTime(value));
      setUnreadable(false);
    }
  }, [value]);

  const commit = () => {
    const parsed = parseTimeInput(text);
    if (parsed === 'invalid') {
      setUnreadable(true);
      return;
    }
    setUnreadable(false);
    setText(parsed ?? '');
    if ((parsed ?? null) !== (value ? formatTime(value) : null)) {
      onCommit(parsed);
    }
  };

  return (
    <TextInput
      id={id}
      size={size}
      mono
      inputMode="numeric"
      autoComplete="off"
      autoFocus={autoFocus}
      value={text}
      placeholder={placeholder}
      disabled={disabled}
      invalid={invalid || unreadable}
      title={unreadable ? 'Not a time. Try 8:30, 830 or 1.30pm. Escape puts back the saved value.' : undefined}
      aria-label={ariaLabel}
      className={`time-input ${className}`.trim()}
      onFocus={(e) => {
        focused.current = true;
        e.currentTarget.select();
      }}
      onChange={(e) => {
        setText(e.target.value);
        if (unreadable) setUnreadable(false);
      }}
      onBlur={() => {
        focused.current = false;
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setText(formatTime(value));
          setUnreadable(false);
          e.currentTarget.blur();
        }
        onKeyDown?.(e);
      }}
    />
  );
};

export default TimeInput;
